import 'flag-icon-css/css/flag-icon.min.css';
import './language.scss';

import {
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
} from '@coreui/react';
import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';

import { useLocalize } from '../../hooks/useLocalize';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { setHtmlTextDirection } from '../../utils/utils';
import { GridModal } from '../gridModal/GridModal';
import {
  ILanguageOption,
  LanguageContext,
} from '../localization/LanguageContext';
import { LanguageModalMobile } from './languageModalMobile/LanguageModalMobile';
import { SearchLanguageItem } from './searchLanguageItem/SearchLanguageItem';

export interface LanguageProps {
  key: string,
  value: string,
  icon: string,
}

const toLanguageProps = (langOption: ILanguageOption) => ({
  key: langOption.language_iso_code,
  value: langOption.language_name,
  icon: langOption.language_flag?.filename,
});

export const Language: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 912px)');
  const { setLocale, languages } = useContext(LanguageContext);
  const [modalOpen, setModalOpen] = useState(false);
  const { locale, translate: t } = useLocalize();
  const navigate = useNavigate();
  const [, setValue] = useLocalStorage('preferredLanguage');
  const arrowStyles: any = {
    '--content-text-color':
      window.tz_globalConfigs?.styling?.backgroundContrastColor || '#000000',
  } as React.CSSProperties;

  const currentLanguage = useMemo(() => languages?.find(
    (obj: ILanguageOption) => obj.language_iso_code === locale,
  ),
  [languages, locale],
  );

  const onLanguageChange = (lng: string) => {
    setValue(lng);
    setLocale?.(lng);
    setHtmlTextDirection(lng);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('language', lng);
    navigate({ pathname: location.pathname, search: `?${urlParams.toString()}` }, { replace: true });
  };

  const navigatorLanguages = (navigator?.languages || (navigator?.language && [navigator?.language]) || []).map(lang => lang.split('-')[0]);

  const suggestedLanguageList = useMemo(
    () => languages
      ? languages
        .filter(
          (langOption: ILanguageOption) => (langOption.language_iso_code === 'en-US'
                    || navigatorLanguages.some(lang => langOption.language_iso_code.startsWith(`${lang}-`))
          )
                  && langOption.language_iso_code
                    !== currentLanguage?.language_iso_code,
        ).sort((lang1, lang2) => lang1.language_name.localeCompare(lang2.language_name))
        .map(toLanguageProps)
      : [],
    [languages, currentLanguage],
  );

  const languagesList = useMemo(() => languages
    ? [
      ...(currentLanguage ? [toLanguageProps(currentLanguage)] : []),
      ...languages
        .map(toLanguageProps)
        .filter((langOption) => !suggestedLanguageList.find(slang => slang.key === langOption.key) && langOption.key !== currentLanguage?.language_iso_code)
        .sort((lang1: any, lang2: any) => lang1.value.localeCompare(lang2.value))]
    : [],
  [languages, currentLanguage],
  );

  const renderLanguageItems = (languages?: LanguageProps[]) => (languages || []).map((language) => (
    <SearchLanguageItem
      key={language.key}
      code={language.key}
      onClick={() => {
        onLanguageChange(language.key);
        setModalOpen(false);
      } }
      icon={language.icon}
      name={language.value}
      selectedItem={currentLanguage?.language_iso_code} />
  ));

  return (
    <>
      {isMobile ? (
        <div>
          <div className="AB-test-new-currency-language">
            <div className="c-header-nav-link mr-2 ml-2">
              <img
                className="flag-icon"
                onClick={() => setModalOpen(true)}
                src={currentLanguage?.language_flag.filename}
              />
            </div>
            <LanguageModalMobile
              modalOpen={modalOpen}
              setModalOpen={setModalOpen}
              languagesList={languagesList}
              suggestedLanguageList={suggestedLanguageList}
              currentLanguage={currentLanguage}
              onLanguageChange={onLanguageChange}
            />
          </div>
          <div className="AB-test-old-currency-language">
            <CDropdown
              inNav
              className="language-container c-header-nav-items mx-2"
            >
              <CDropdownToggle
                className="c-header-nav-link"
                color="secondary"
                caret={true}
                style={arrowStyles}
              >
                <img
                  className="flag-icon"
                  src={currentLanguage?.language_flag.filename}
                />
              </CDropdownToggle>
              <CDropdownMenu placement="bottom-end" className="m-0">
                {languages?.map((obj: ILanguageOption) => locale !== `${obj.language_iso_code}` ? (
                  <CDropdownItem
                      key={obj.language_iso_code}
                      onClick={() => onLanguageChange(`${obj.language_iso_code}`)}
                  >
                    <img
                        className="mx-4 flag-icon"
                        src={obj.language_flag?.filename}
                    />
                    {obj.language_name}
                  </CDropdownItem>
                ) : null,
                )}
              </CDropdownMenu>
            </CDropdown>
          </div>
        </div>
      ) : (
        <>
          <CDropdown inNav className="language-container c-header-nav-items mx-2 AB-test-old-currency-language">
            <CDropdownToggle
              className="c-header-nav-link"
              color="secondary"
              caret={true}
              style={arrowStyles}
            >
              <img className="flag-icon" src={currentLanguage?.language_flag.filename} />
            </CDropdownToggle>
            <CDropdownMenu placement="bottom-end" className="m-0">
              {languages?.map((obj: ILanguageOption) => locale !== `${obj.language_iso_code}` ? (
                <CDropdownItem
                    key={obj.language_iso_code}
                    onClick={() => onLanguageChange(`${obj.language_iso_code}`)}
                >
                  <img
                      className="mx-4 flag-icon"
                      src={obj.language_flag?.filename}
                  />
                  {obj.language_name}
                </CDropdownItem>
              ) : null,
              )}
            </CDropdownMenu>
          </CDropdown>

          <CDropdown inNav className="language-container c-header-nav-items mx-2 AB-test-new-currency-language">
            <CDropdownToggle
              className="c-header-nav-link"
              color="secondary"
              caret={true}
              style={arrowStyles}
              onClick={() => {
                setModalOpen(value => !value);
              }}
            >
              <img className="flag-icon" src={currentLanguage?.language_flag.filename} />
            </CDropdownToggle>
            <GridModal
              show={modalOpen}
              suggestedItems={renderLanguageItems(suggestedLanguageList)}
              items={
                renderLanguageItems(languagesList)
              }
              modalTitle={t('select_your_language')}
              suggestedTitle={t('suggested_languages')}
              allTitle={t('all_languages')}
              onClose={() => {
                setModalOpen(false);
              }}
            />
          </CDropdown>
        </>
      )}
    </>
  );
};
